import { ListBaseModel } from '@/api/models';
import { t } from '@/i18n';
import { isPS, passwordType } from '@/utils';
import { z } from 'zod';

/// ////////////////////////////////////////////
/// Model
/// ////////////////////////////////////////////

const OrganizationInUserModel = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

export const UserBaseModel = z.object({
  id: z.string().uuid(),
  name: z.string(),
  // public site user response hasn't email
  email: isPS() ? z.void() : z.string().email(),
  iconDataUrl: z.string().nullish(),
  organizationIds: z.string().uuid().array().nullable(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  modifiedAt: z.string().nullable().optional(),
  isIntegrated: z.boolean(),
  isDeleted: z.boolean(),
  isEmailNotificationEnabled: z.boolean(),
});

export const UserModel = UserBaseModel.extend({
  role: z.string().nullish(),
  organizations: OrganizationInUserModel.array().nullable(),
}).transform((data) => {
  return {
    ...data,
    name:
      data.isDeleted && !isPS()
        ? t('deleted-user', { name: data.name })
        : data.name,
  };
});

export const UsersLightModel = z
  .object({
    id: z.string(),
    name: z.string(),
    organizationIds: z.string().uuid().array().nullable(),
    isIntegrated: z.boolean(),
    isDeleted: z.boolean(),
    email: z.string().nullish(),
    organizations: z
      .object({
        id: z.string(),
        name: z.string(),
        code: z.string().nullish(),
      })
      .array(),
  })
  .array();

export const UsersModel = UserModel.array();
export const UsersListModel = ListBaseModel.extend({
  results: UsersModel,
});

const UserWithUniqueOrganizationModel = UserBaseModel.pick({
  id: true,
  email: true,
  name: true,
}).extend({
  organizationStructures: OrganizationInUserModel.array(),
});
const UsersWithUniqueOrganizationModel =
  UserWithUniqueOrganizationModel.array();
export const UsersWithUniqueOrganizationListModel = ListBaseModel.extend({
  results: UsersWithUniqueOrganizationModel,
});

/// ////////////////////////////////////////////
/// Schema
/// ////////////////////////////////////////////

export const UserSearchFormSchema = z.object({
  name: z.string().nullish(),
  email: z.string().nullish(),
});

export const UserFormSchema = UserBaseModel.pick({
  name: true,
  email: true,
  isEmailNotificationEnabled: true,
});

export const ResetPasswordSchema = z
  .object({
    newPassword: passwordType,
    confirmPassword: passwordType,
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: t('validation.password-must-match'),
  });

export const UserOptionModel = z
  .object({
    id: z.string(),
    organizationIds: z.string().array().nullish(),
    organizations: z
      .object({
        id: z.string(),
        name: z.string(),
      })
      .array()
      .nullish(),
    name: z.string(),
  })
  .array();

export const SearchUsersModalFormSchema = z.object({
  userName: z.string().nullish(),
  organizationIds: z.array(z.string().uuid().array()).nullish(),
});

export const UsersCsvUploadRunMode = z.enum(['dry-run', 'save']);

export const UsersForBulkUpdateSchema = z
  .object({
    name: z.string(),
    email: z.string(),
    organizations: z
      .object({
        name: z.string(),
        flattenExternalSystemCode: z.string(),
      })
      .array(),
  })
  .array();

export const BulkUpdateUsersSchema = z.object({
  runMode: UsersCsvUploadRunMode,
  csvData: UsersForBulkUpdateSchema,
});
